import { useEffect, useRef, useState } from 'react';
import { filter } from 'rxjs';
import { SupportCasesItem } from '../../../types';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { useSearchParams } from 'react-router-dom';
import { DetailsDrawer } from '../../Compounds/DetailsDrawer';
import { getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { SupportCaseWizard } from '../../Compounds/Wizards/SupportCaseWizard';
import { getCaseLoadingSubject, getCaseSubject, OPEN_STATUS, useSupportCases } from '../../../store/SupportCase';
import { DashboardWidgets } from '../../Compounds/DashboardWidgets/DashboardWidgets';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { Permission } from '../../../store/GeneralStore';
import {
  GridPreferences,
  getDefaultPreferences,
  getPreferencesByKey,
  getUserPreferencesSubject
} from '../../../store/User';
import { CustomizeDashboardDrawer } from '../../Compounds/CustomizeDashboardDrawer';
import { Tab, Tabs, useTheme } from '@mui/material';
import { filteredClosedCasesByMonth } from '../../../utils/helpers';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { DefaultCaseGridColumnDefs } from './SupportCaseGridColDefs';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import { TabPanel } from '../../Atoms/TabPanel';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';

export const SupportCasesPage = () => {
  const page = 'Cases';
  const [cases, setCases] = useState<SupportCasesItem[]>([]);
  const [casesLoading, setCasesLoading] = useState<boolean>(false);
  const [search] = useSearchParams();
  const detailId = search.get('detailId');
  const { fetchCases } = useSupportCases();
  const [selectedTab, setSelectedTab] = useState(0);
  // The case subscription is set up once, so it reads the tab here rather than the value it closed over.
  const selectedTabRef = useRef(0);
  const [columns, setColumns] = useState<GridSingleSelectColDef[]>();
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences().content['case_grid'] as GridPreferences
  );
  const [tabs, setTabs] = useState<{ name: string; cases: SupportCasesItem[] }[]>([]);
  const theme = useTheme();

  // Subscribe to and load data.
  useEffect(() => {
    const caseStore = getCaseSubject()
      // The store's starting value is replayed on subscribe; a later empty list is an account with no cases.
      .pipe(filter((caseData, index) => index > 0 || !!caseData?.length))
      .subscribe((caseData) => {
        const rows = caseData || [];
        setCases(rows);

        const tabs = [
          { name: 'Open', cases: rows.filter((item) => OPEN_STATUS.includes(item.status)) },
          { name: 'Recently Closed', cases: filteredClosedCasesByMonth(rows, 2) },
          { name: 'All', cases: rows }
        ];

        setTabs(tabs);
        setColumns(getGridColumns(tabs?.[selectedTabRef.current]?.cases, DefaultCaseGridColumnDefs, 'case_grid'));
      });
    const loadingSubscription = getCaseLoadingSubject().subscribe((loading) => setCasesLoading(loading));
    const settingsSub = getUserPreferencesSubject().subscribe(() => {
      const gridPrefs = getPreferencesByKey('case_grid') as GridPreferences;
      if (gridPrefs) setGridSettings(gridPrefs);
    });

    fetchCases();

    return () => {
      if (caseStore) caseStore?.unsubscribe();
      if (loadingSubscription) loadingSubscription.unsubscribe();
      if (settingsSub) settingsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (tabs.length) handleTabChange(0);
  }, [tabs.length]);

  const handleTabChange = (index: number) => {
    selectedTabRef.current = index;
    setSelectedTab(index);
    const columns = getGridColumns(tabs?.[index]?.cases, DefaultCaseGridColumnDefs, 'case_grid');
    setColumns(columns);
  };

  const tableCardActions = () => (
    <PermissibleRender requiredPermissions={[Permission.CREATE_CASES_FROM_CASES]}>
      <SupportCaseWizard
        ctaClassName="text-grey-12 text-sm"
        onFormSubmissionHandler={async () => await fetchCases(true)}
      />
    </PermissibleRender>
  );

  const cardActions = () => <CustomizeDashboardDrawer title="Support" />;

  return (
    <PageLayout
      dataCy="cases-page-card"
      pageTitle="Support">
      <>
        <PageCard
          title="Dashboard"
          cardActions={cardActions()}>
          <DashboardWidgets
            inventoryAssets={[]}
            pageTitle={page}
            cases={cases}
            projects={[]}
          />
        </PageCard>

        <PageCard
          childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
          dataCy="cases-management-card"
          title={page}
          cardActions={tableCardActions()}>
          <>
            <Tabs
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#F9F9F9',
                margin: '-20px',
                '.Mui-selected': {
                  backgroundColor: 'white',
                  color: theme.palette.primary.main
                }
              }}
              TabIndicatorProps={{
                sx: {
                  backgroundColor: 'white',
                  height: selectedTab <= tabs.length - 1 ? 2 : 0,
                  borderRadius: 1
                }
              }}
              value={selectedTab}
              onChange={(_e, index) => handleTabChange(index)}>
              {tabs.map((tab, index) => (
                <Tab
                  data-cy={`tab-link-${tab.name}`}
                  sx={{ textTransform: 'none', fontSize: '.8rem', color: 'black' }}
                  key={index}
                  label={`${tab.name} (${tab.cases.length})`}
                />
              ))}
            </Tabs>
            {tabs.map((tab, index) => (
              <TabPanel
                key={`${index}`}
                index={index}
                value={selectedTab}>
                <UpstackDataGrid
                  dataCy={`cases-${tab.name}-grid`}
                  title={`Support Cases - ${tab.name}`}
                  rows={tab.cases}
                  columns={columns}
                  pinnedColumns={{ left: ['details', 'account_id', 'sfId'] }}
                  loadingData={casesLoading}
                  page="case_grid"
                  gridSettings={gridSettings}
                  showSearch={true}
                  rowPrimaryKeys={['id']}
                />
              </TabPanel>
            ))}
            {casesLoading && (
              <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                <ComponentSpinner />
              </div>
            )}
          </>
        </PageCard>
        <div className="h-2"></div>
        <DetailsDrawer
          data={cases?.find((t) => t.id === detailId) || ({} as SupportCasesItem)}
          showDrawer={!!detailId}
          link={`/support-cases/${detailId}`}
          {...getMetaData('support')}
        />
      </>
    </PageLayout>
  );
};
